// 🗑️ 애플리케이션 삭제 관리 훅
import { useState, useCallback } from 'react';
import type { DeleteDialogState } from '../types/applicationStickyHeader.types';

export interface UseApplicationDeleteReturn extends DeleteDialogState {
  setDeleteDialogOpen: (open: boolean) => void;
  setIsDeleting: (deleting: boolean) => void;
  handleDeleteClick: () => void;
  handleDeleteCancel: () => void;
  handleDeleteConfirm: () => Promise<void>;
}

/**
 * 애플리케이션 삭제 기능을 관리하는 훅
 * - 삭제 확인 다이얼로그 관리
 * - 삭제 실행 및 상태 관리
 * - 삭제 취소 처리
 */
export const useApplicationDelete = (
  onDelete?: () => Promise<void>
): UseApplicationDeleteReturn => {
  // 🗑️ 삭제 기능 상태
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // 🗑️ 삭제 다이얼로그 열기
  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  // 🗑️ 삭제 다이얼로그 닫기
  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  // 🗑️ 삭제 확인 핸들러
  const handleDeleteConfirm = useCallback(async () => {
    if (!onDelete) return;

    setIsDeleting(true);
    try {
      await onDelete();
      setDeleteDialogOpen(false);
      // 삭제 성공 시 자동으로 목록 페이지로 이동 (onDelete에서 처리)
    } catch (error) {
      console.error('삭제 실패:', error);
      setDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete]);

  return {
    open: deleteDialogOpen,
    deleting: isDeleting,
    setDeleteDialogOpen,
    setIsDeleting,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
  };
};